import { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Image as ImageIcon, Plus, Trash2, X } from 'lucide-react'

import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { Breadcrumb } from '../../components/ui/Breadcrumb'
import { shellAssets } from '../../constants/assets'
import { dashboardRoutes } from '../../constants/routes'
import {
  apiGetServiceById,
  apiCreateService,
  apiUpdateService,
} from '../../api/services/servicesApi'
import type { VehicleCategory } from '../../types'

const currentUser = {
  name: 'Admin',
  email: 'Admin@123gmail.com',
  avatar: shellAssets.avatar,
}

const defaultFeatures = [
  'Family Friendly',
  'Après Ski',
  'Free Waiting Time',
  'Snowboarding',
  'Winter Hiking',
  'Fuel Included',
  'Modern Ski Lifts',
  'Ski Schools',
  'Hotel Pickup',
]

const defaultExcludedFeatures = [
  'Food & Drinks',
  'Entry Tickets & Admission',
  'Gratuities / Tips',
  'Personal Expenses',
  'Ski Equipment Rental',
  'Ski Lift Passes',
  'Hotel Accommodation',
]

const defaultGoodToKnow = [
  'Two-way private car transfer',
  'Lunch Included',
  'Air-conditioned vehicle',
  'All Fees & Taxes',
  'Professional Driver',
  'Entry & Admission Fees',
]

const vehicleOptions: VehicleCategory[] = [
  'Standard Saloon',
  'Premium Saloon',
  'Economy Van',
  'Minibus',
  'Minivan' as VehicleCategory,
]

// Helper component for Highlight blocks
const HighlightBlock = ({
  title,
  availableItems,
  setAvailableItems,
  selectedItems,
  setSelectedItems,
  addLabel,
}: {
  title: string,
  availableItems: string[],
  setAvailableItems: React.Dispatch<React.SetStateAction<string[]>>,
  selectedItems: string[],
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>,
  addLabel?: string,
}) => {
  const [isRemoving, setIsRemoving] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [newItem, setNewItem] = useState('')

  const handleToggle = (item: string) => {
    setSelectedItems(prev => prev.includes(item)
      ? prev.filter(i => i !== item)
      : [...prev, item]
    )
  }

  const handleDelete = (item: string) => {
    setAvailableItems(prev => prev.filter(i => i !== item))
    setSelectedItems(prev => prev.filter(i => i !== item))
  }

  const handleAddNew = () => {
    const trimmed = newItem.trim()
    if (trimmed && !availableItems.includes(trimmed)) {
      setAvailableItems(prev => [...prev, trimmed])
      setSelectedItems(prev => [...prev, trimmed])
    }
    setNewItem('')
    setIsAdding(false)
  }

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-host text-[14px] font-bold text-[#1A1A1A]">{title}</h2>
        <div className="flex items-center gap-3">
          {!isRemoving && !isAdding && (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="flex items-center gap-1.5 rounded-[6px] bg-[#FFE8E0] px-3 py-1.5 text-[12px] font-semibold text-[#FF6A1A] hover:bg-[#FFD3C2] transition-colors"
            >
              <Plus size={14} strokeWidth={2.5} />
              {addLabel || 'Add Item'}
            </button>
          )}

          {isAdding && (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newItem}
                onChange={e => setNewItem(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddNew()}
                placeholder="New item..."
                autoFocus
                className="h-[28px] rounded-[4px] border border-[#E0E0E0] px-2 text-[12px] focus:border-[#FF6A1A] focus:outline-none"
              />
              <button
                type="button"
                onClick={handleAddNew}
                className="rounded-[4px] bg-[#FF6A1A] px-2 py-1 text-[12px] font-semibold text-white"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => { setIsAdding(false); setNewItem(''); }}
                className="text-[12px] text-[#757575] hover:text-[#1A1A1A]"
              >
                Cancel
              </button>
            </div>
          )}

          {!isAdding && (
            <button
              type="button"
              onClick={() => setIsRemoving(!isRemoving)}
              className={`flex items-center gap-1.5 rounded-[6px] border px-3 py-1.5 text-[12px] font-semibold transition-colors ${isRemoving
                  ? 'bg-[#E0E0E0] border-[#E0E0E0] text-[#1A1A1A] hover:bg-[#D6D6D6]'
                  : 'bg-white border-[#E0E0E0] text-[#1A1A1A] hover:bg-[#F5F5F5]'
                }`}
            >
              {!isRemoving && <Trash2 size={14} />}
              {isRemoving ? 'Done' : 'Remove'}
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-2">
        {availableItems.map((item) => (
          <div key={item} className="flex items-center justify-between">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedItems.includes(item)}
                onChange={() => handleToggle(item)}
                disabled={isRemoving}
                className="size-[18px] rounded-[4px] border-[#E0E0E0] text-[#FF6A1A] focus:ring-[#FF6A1A] cursor-pointer disabled:opacity-50"
              />
              <span className={`font-host text-[13px] ${isRemoving ? 'text-[#757575]' : 'text-[#1A1A1A]'}`}>
                {item}
              </span>
            </label>
            {isRemoving && (
              <button
                type="button"
                onClick={() => handleDelete(item)}
                className="text-[#FF6A1A] hover:text-red-600 transition-colors"
                title="Remove from list"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export const ServiceFormPage = () => {
  const navigate = useNavigate()
  const { id, category } = useParams()
  const isEditing = Boolean(id)
  const computedCategory =
    category ||
    (id?.startsWith('private-tour-')
      ? 'private-van-tours'
      : id?.startsWith('ski-transfer-')
        ? 'ski-transfers'
        : 'day-trips')

  const [availableFeatures, setAvailableFeatures] = useState(defaultFeatures)
  const [availableExcludedFeatures, setAvailableExcludedFeatures] = useState(defaultExcludedFeatures)
  const [availableGoodToKnow, setAvailableGoodToKnow] = useState(defaultGoodToKnow)

  const [formData, setFormData] = useState({
    title: '', // Trip name 1
    startLocation: '',
    endLocation: '',
    startLocation2: '', // Trip name 2
    endLocation2: '',
    subtitle: '',
    description: '',
    itinerary: '',
    people: '',
    duration: '',
    durationNights: '',
    distance: '',
    basePrice: '',
    extraPrice: '',
    active: true,
    showOnHomepage: false,
    features: [] as string[],
    excludedFeatures: [] as string[],
    goodToKnow: [] as string[],
    vehicles: [] as VehicleCategory[],
    images: [] as string[],
  })

  const mainFileInputRef = useRef<HTMLInputElement>(null)
  const thumbFileInputRef = useRef<HTMLInputElement>(null)

  // Track which thumbnail index we are uploading
  const [uploadIndex, setUploadIndex] = useState<number | null>(null)

  const [imageFiles, setImageFiles] = useState<(File | null)[]>([])

  const imageObjectUrlsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    return () => {
      imageObjectUrlsRef.current.forEach(url => URL.revokeObjectURL(url))
      imageObjectUrlsRef.current.clear()
    }
  }, [])

  useEffect(() => {
    if (!isEditing || !id) {
      return
    }

    let cancelled = false

    const loadService = async () => {
      const response = await apiGetServiceById(
        id,
        computedCategory as
        | 'day-trips'
        | 'private-van-tours'
        | 'ski-transfers',
      )

      if (cancelled) return

      if (!response.success || !response.data) {
        console.error('[ServiceFormPage] Failed to load service:', response.message)
        window.alert(response.message || 'Failed to load service.')
        return
      }

      const service: any = response.data
      setImageFiles([])

      setFormData({
        title: service.title || '',
        startLocation: service.startLocation ?? service.start_location ?? '',
        endLocation: service.endLocation ?? service.end_location ?? '',
        startLocation2: service.startLocation2 ?? service.start_location2 ?? '',
        endLocation2: service.endLocation2 ?? service.end_location2 ?? '',
        subtitle: service.subtitle ?? service.badge ?? '',
        description: service.description ?? '',
        itinerary: service.itinerary ?? '',
        people: service.people !== undefined ? String(service.people) : '',
        duration: service.duration !== undefined ? String(service.duration) : '',
        durationNights: service.durationNights !== undefined ? String(service.durationNights) : '',
        distance: service.distance ?? '',
        basePrice: service.basePrice !== undefined ? String(service.basePrice) : service.priceEur !== undefined ? String(service.priceEur) : '',
        extraPrice: service.extraPrice !== undefined ? String(service.extraPrice) : service.extraPriceEur !== undefined ? String(service.extraPriceEur) : '0',
        active: service.active ?? service.is_active ?? true,
        showOnHomepage: service.showOnHomepage ?? service.show_on_homepage ?? false,
        features: Array.isArray(service.features) ? service.features : [],
        excludedFeatures: Array.isArray(service.excludedFeatures)
          ? service.excludedFeatures
          : Array.isArray(service.excluded_features)
            ? service.excluded_features
            : [],
        goodToKnow: Array.isArray(service.goodToKnow) ? service.goodToKnow : Array.isArray(service.good_to_know) ? service.good_to_know : [],
        vehicles: Array.isArray(service.vehicleAssignment) ? service.vehicleAssignment : Array.isArray(service.vehicle_assignment) ? service.vehicle_assignment : [],
        images: Array.isArray(service.images) ? service.images : [],
      })

      if (Array.isArray(service.features)) {
        setAvailableFeatures(prev => [
          ...prev,
          ...service.features.filter((feature: string) => !prev.includes(feature)),
        ])
      }

      const excluded = Array.isArray(service.excludedFeatures)
        ? service.excludedFeatures
        : Array.isArray(service.excluded_features)
          ? service.excluded_features
          : []

      if (excluded.length) {
        setAvailableExcludedFeatures(prev => [
          ...prev,
          ...excluded.filter((item: string) => !prev.includes(item)),
        ])
      }

      const goodToKnow = Array.isArray(service.goodToKnow)
        ? service.goodToKnow
        : Array.isArray(service.good_to_know)
          ? service.good_to_know
          : []

      if (goodToKnow.length) {
        setAvailableGoodToKnow(prev => [
          ...prev,
          ...goodToKnow.filter((item: string) => !prev.includes(item)),
        ])
      }
    }

    loadService()

    return () => {
      cancelled = true
    }
  }, [id, isEditing, computedCategory])

  // const handleSave = async () => {
  //   const data = {
  //     title: formData.title,
  //     startLocation: formData.startLocation,
  //     endLocation: formData.endLocation,
  //     startLocation2: formData.startLocation2,
  //     endLocation2: formData.endLocation2,
  //     subtitle: formData.subtitle,
  //     description: formData.description,
  //     people: parseInt(formData.people) || 0,
  //     duration:
  //       computedCategory === 'ski-transfers' || computedCategory === 'day-trips'
  //         ? formData.duration
  //         : parseInt(formData.duration) || 0,
  //     durationNights:
  //       computedCategory === 'ski-transfers' || computedCategory === 'day-trips'
  //         ? 0
  //         : parseInt(formData.durationNights) || 0,
  //     distance: formData.distance,
  //     basePrice: parseFloat(formData.basePrice) || 0,
  //     extraPrice: parseFloat(formData.extraPrice) || 0,
  //     active: formData.active,
  //     showOnHomepage: formData.showOnHomepage,
  //     show_on_homepage: formData.showOnHomepage,
  //     is_active: formData.active,
  //     features: formData.features,
  //     goodToKnow: formData.goodToKnow,
  //     vehicleAssignment: formData.vehicles,
  //     images: formData.images.filter(image => image !== '' && !image.startsWith('blob:')),
  //     imageFiles,
  //   }

  //   const response =
  //     isEditing && id
  //       ? await apiUpdateService(
  //         computedCategory as 'day-trips' | 'private-van-tours' | 'ski-transfers',
  //         id,
  //         data,
  //       )
  //       : await apiCreateService(
  //         computedCategory as 'day-trips' | 'private-van-tours' | 'ski-transfers',
  //         data,
  //       )

  //   if (!response.success) {
  //     console.error('[ServiceFormPage] Save failed:', response)
  //     const errors = response.errors ? `\n${JSON.stringify(response.errors, null, 2)}` : ''
  //     window.alert(`${response.message || 'Failed to save service.'}${errors}`)
  //     return
  //   }

  //   window.alert(isEditing ? 'Service updated successfully.' : 'Service created successfully.')
  //   navigate(dashboardRoutes.services)
  // }
  const handleSave = async () => {
    const selectedImages = formData.images.filter(
      image => image.trim() !== ''
    )

    if (selectedImages.length > 5) {
      window.alert(
        'You can upload a maximum of 5 images, including the main image.'
      )
      return
    }
    const data = {
      title: formData.title,
      startLocation: formData.startLocation,
      endLocation: formData.endLocation,
      startLocation2: formData.startLocation2,
      endLocation2: formData.endLocation2,
      subtitle: formData.subtitle,
      description: formData.description,
      // ...(computedCategory === 'day-trips'
      //   ? { itinerary: formData.itinerary }
      //   : {}),

      itinerary: formData.itinerary,
      // Optional numeric field: keep empty instead of forcing 0
      people: formData.people === '' ? '' : parseInt(formData.people) || 0,

      // Day Trips + Ski Transfers use text duration.
      // Private Van Tours use numeric duration.
      duration:
        computedCategory === 'ski-transfers' || computedCategory === 'day-trips'
          ? formData.duration
          : formData.duration === ''
            ? ''
            : parseInt(formData.duration) || 0,

      // Optional field: keep empty instead of forcing 0
      durationNights:
        computedCategory === 'ski-transfers' || computedCategory === 'day-trips'
          ? 0
          : formData.durationNights === ''
            ? ''
            : parseInt(formData.durationNights) || 0,

      // Distance is not used by Day Trips.
      // Keep existing behavior for Ski Transfers and Private Van Tours.
      ...(computedCategory !== 'day-trips'
        ? {
            distance: formData.distance,
          }
        : {}),

      // Optional pricing fields: preserve empty values
     // Private Van Tours do not use pricing.
    ...(computedCategory !== 'private-van-tours'
      ? {
          basePrice:
            formData.basePrice === ''
              ? ''
              : parseFloat(formData.basePrice) || 0,

          extraPrice:
            formData.extraPrice === ''
              ? ''
              : parseFloat(formData.extraPrice) || 0,
        }
      : {}),

      active: formData.active,
      showOnHomepage: formData.showOnHomepage,
      show_on_homepage: formData.showOnHomepage,
      is_active: formData.active,

      features: formData.features,
      excludedFeatures: formData.excludedFeatures,
      excluded_features: formData.excludedFeatures,
      goodToKnow: formData.goodToKnow,
      vehicleAssignment: formData.vehicles,

      images: formData.images.filter(
        image => image !== '' && !image.startsWith('blob:')
      ),

      imageFiles,
    }

    const response =
      isEditing && id
        ? await apiUpdateService(
            computedCategory as
              | 'day-trips'
              | 'private-van-tours'
              | 'ski-transfers',
            id,
            data,
          )
        : await apiCreateService(
            computedCategory as
              | 'day-trips'
              | 'private-van-tours'
              | 'ski-transfers',
            data,
          )

    if (!response.success) {
      console.error('[ServiceFormPage] Save failed:', response)

      const errors = response.errors
        ? `\n${JSON.stringify(response.errors, null, 2)}`
        : ''

      window.alert(
        `${response.message || 'Failed to save service.'}${errors}`
      )

      return
    }

    window.alert(
      isEditing
        ? 'Service updated successfully.'
        : 'Service created successfully.'
    )

    navigate(dashboardRoutes.services)
  }

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    isMain: boolean,
  ) => {
    const file = e.currentTarget.files?.[0]
    const targetIndex = isMain ? 0 : uploadIndex

    // Allow selecting the same file again.
    e.currentTarget.value = ''
    setUploadIndex(null)

    if (!file || targetIndex === null || targetIndex < 0 || targetIndex > 4) return

    const previousUrl = formData.images[targetIndex]
    const previewUrl = URL.createObjectURL(file)
    imageObjectUrlsRef.current.add(previewUrl)

    setFormData(prev => {
      const images = Array.from({ length: 6 }, (_, i) => prev.images[i] ?? '')
      images[targetIndex] = previewUrl
      return { ...prev, images }
    })

    setImageFiles(prev => {
      const files = Array.from({ length: 6 }, (_, i) => prev[i] ?? null)
      files[targetIndex] = file
      return files
    })

    if (previousUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(previousUrl)
      imageObjectUrlsRef.current.delete(previousUrl)
    }
  }

  const handleRemoveImage = (index: number) => {
    if (index < 0 || index > 5) return

    const previousUrl = formData.images[index]

    setFormData(prev => {
      const images = Array.from({ length: 6 }, (_, i) => prev.images[i] ?? '')
      images[index] = ''
      return { ...prev, images }
    })

    setImageFiles(prev => {
      const files = Array.from({ length: 6 }, (_, i) => prev[i] ?? null)
      files[index] = null
      return files
    })

    setUploadIndex(null)

    if (previousUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(previousUrl)
      imageObjectUrlsRef.current.delete(previousUrl)
    }
  }

  const renderRemoveImageButton = (index: number, small = false) => (
    <button
      type="button"
      aria-label={index === 0 ? 'Remove main image' : 'Remove gallery image ' + index}
      title="Remove image"
      onClick={event => {
        event.stopPropagation()
        handleRemoveImage(index)
      }}
      className={`absolute z-10 flex items-center justify-center rounded-full bg-white text-[#403E3E] shadow-md hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6A1A] ${
        small
          ? 'right-[2px] top-[2px] size-[16px]'
          : 'right-[6px] top-[6px] size-[24px]'
      }`}
    >
      <X size={small ? 11 : 14} strokeWidth={2.5} aria-hidden="true" />
    </button>
  )

  const toggleVehicle = (v: VehicleCategory) => {
    setFormData(prev => ({
      ...prev,
      vehicles: prev.vehicles.includes(v)
        ? prev.vehicles.filter(x => x !== v)
        : [...prev.vehicles, v]
    }))
  }

  // Maximum 5 images: 1 main image + 4 gallery images.
  // Main image is images[0]; gallery images are images[1..4].
  const renderThumbnails = () => {
    const thumbs = []
      for (let i = 1; i <= 4; i++) {
      const hasImage = formData.images[i]
      thumbs.push(
        <div
          key={i}
          onClick={() => { setUploadIndex(i); thumbFileInputRef.current?.click(); }}
          className={`group relative flex size-[48px] items-center justify-center rounded-[8px] border-2 border-dashed bg-[#F9FBE7] transition-colors hover:bg-[#F1F8E9] cursor-pointer overflow-hidden ${hasImage ? 'border-transparent' : 'border-[#B2D873]'}`}
        >
          {hasImage ? (
            <>
              <img
                src={formData.images[i]}
                alt="thumb"
                className="w-full h-full object-cover"
              />
              {renderRemoveImageButton(i, true)}
            </>
          ) : (
            <ImageIcon className="text-[#1A1A1A]" size={16} />
          )}
        </div>
      )
    }
    return thumbs
  }

  let breadcrumbLabel = 'Day Trips'
  if (computedCategory === 'ski-transfers') breadcrumbLabel = 'Ski Transfers'
  if (computedCategory === 'private-van-tours') breadcrumbLabel = 'Private Van Tours'

  return (
    <DashboardLayout
      activeNavId="services"
      contentGap={15}
      subtitle={
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: dashboardRoutes.overview },
            { label: breadcrumbLabel, path: dashboardRoutes.services },
            { label: isEditing ? 'Edit Service' : 'New Service' }
          ]}
        />
      }
      title="Services"
      user={currentUser}
    >
      <div
          className={`flex flex-col gap-6 lg:flex-row items-start ${
            computedCategory === 'private-van-tours'
              ? 'lg:items-stretch'
              : ''
          }`}
        >
        {/* Left Column */}
        <div className="flex w-full flex-col gap-6 lg:w-[45%] rounded-[16px] bg-white p-[24px] shadow-sm">

          {/* Starting Location Row 1 */}
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-bold text-[#1A1A1A]">Trip Name</label>
            <input
              type="text"
              placeholder="Enter Trip Name"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-bold text-[#1A1A1A]">Starting Location</label>
              <input
                type="text"
                placeholder="Enter Starting Location"
                value={formData.startLocation}
                onChange={(e) => setFormData({ ...formData, startLocation: e.target.value })}
                className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-bold text-[#1A1A1A]">Ending Location</label>
              <input
                type="text"
                placeholder="Enter Ending Location"
                value={formData.endLocation}
                onChange={(e) => setFormData({ ...formData, endLocation: e.target.value })}
                className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
              />
            </div>
          </div>

          {/* Subtitle */}
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-bold text-[#1A1A1A]">Subtitle</label>
            <input
              type="text"
              placeholder="Transfer"
              value={formData.subtitle}
              onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
              className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
            />
          </div>

          {/* Short Description */}
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-bold text-[#1A1A1A]">Short Description</label>
            <textarea
              placeholder="Describe this service..."
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              maxLength={1500}
              className="min-h-[100px] resize-none rounded-[8px] border border-[#E0E0E0] bg-white p-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
            />

            <span className="font-host text-[11px] text-[#9E9E9E]">
              (Max 1500 Characters)
            </span>
          </div>
          {/* Itinerary for Day Trips */}
          {/* {computedCategory === 'day-trips' && ( */}
            <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-bold text-[#1A1A1A]">
                Itinerary
              </label>
              <textarea
                placeholder="Enter itinerary..."
                value={formData.itinerary}
                onChange={(e) =>
                  setFormData({ ...formData, itinerary: e.target.value })
                }
                className="min-h-[140px] resize-none rounded-[8px] border border-[#E0E0E0] bg-white p-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
              />
            </div>
          {/* )} */}

          {/* People, Duration, Distance */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-bold text-[#1A1A1A]">People</label>
              <input
                type="text"
                placeholder="12"
                value={formData.people}
                onChange={(e) => setFormData({ ...formData, people: e.target.value })}
                className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-bold text-[#1A1A1A]">Tour Duration</label>
              {computedCategory === 'ski-transfers' || computedCategory === 'day-trips' ? (
                <select
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
                >
                  <option value="" disabled>Select Duration</option>

                  <option value="30 Minutes">30 Minutes</option>

                  {Array.from({ length: 12 }, (_, i) => {
                    const hours = i + 1
                    return (
                      <option key={hours} value={`${hours} ${hours === 1 ? 'Hour' : 'Hours'}`}>
                        {hours} {hours === 1 ? 'Hour' : 'Hours'}
                      </option>
                    )
                  })}
                </select>
              ) : (
                <div className="flex items-center justify-between h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4">
                  <div className="flex items-center gap-1.5 w-[50%] border-r border-[#E0E0E0]">
                    <span className="text-[14px]">☀️</span>
                    <input
                      type="number"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      className="w-full font-host text-[14px] text-[#1A1A1A] focus:outline-none bg-transparent"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 w-[50%] pl-3">
                    <span className="text-[14px]">🌙</span>
                    <input
                      type="number"
                      value={formData.durationNights}
                      onChange={(e) => setFormData({ ...formData, durationNights: e.target.value })}
                      className="w-full font-host text-[14px] text-[#1A1A1A] focus:outline-none bg-transparent"
                    />
                  </div>
                </div>
              )}
            </div>

            {computedCategory !== 'day-trips' && (
              <div className="flex flex-col gap-1.5">
                <label className="font-host text-[13px] font-bold text-[#1A1A1A]">Distance</label>
                <input
                  type="text"
                  placeholder="105 km"
                  value={formData.distance}
                  onChange={(e) => setFormData({ ...formData, distance: e.target.value })}
                  className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
                />
              </div>
            )}
          </div>

          {/* Pricing */}
          {/* Pricing — hidden for Private Van Tours */}
          {computedCategory !== 'private-van-tours' && (
            <div className="flex flex-col gap-2 mt-2">
              <h2 className="font-host text-[14px] font-bold text-[#1A1A1A]">
                Pricing
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5 rounded-[8px] border border-[#E0E0E0] p-3">
                  <span className="font-host text-[12px] text-[#757575]">
                    Base Price (€)
                  </span>

                  <div className="flex items-center gap-1 font-host text-[15px] font-medium text-[#1A1A1A]">
                    €
                    <input
                      type="number"
                      value={formData.basePrice}
                      onChange={(e) =>
                        setFormData({ ...formData, basePrice: e.target.value })
                      }
                      className="w-full bg-transparent focus:outline-none"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5 rounded-[8px] border border-[#E0E0E0] p-3">
                  <span className="font-host text-[12px] text-[#757575]">
                    Extra Price (€)
                  </span>

                  <div className="flex items-center gap-1 font-host text-[15px] font-medium text-[#1A1A1A]">
                    €
                    <input
                      type="number"
                      value={formData.extraPrice}
                      onChange={(e) =>
                        setFormData({ ...formData, extraPrice: e.target.value })
                      }
                      className="w-full bg-transparent focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Availability */}
          <div className="flex flex-col gap-3 mt-4">
            <h2 className="font-host text-[14px] font-bold text-[#1A1A1A]">Availability</h2>
            <span className="font-host text-[12px] text-[#757575]">Service Status</span>

            <label className="flex w-fit items-center gap-3 cursor-pointer rounded-[8px] border border-[#8BC34A] bg-[#F1F8E9] px-4 py-2.5">
              <div className={`flex size-5 items-center justify-center rounded-full border-[6px] ${formData.active ? 'border-[#8BC34A] bg-white' : 'border-[#E0E0E0] bg-white'}`}></div>
              <span className="font-host text-[14px] font-bold text-[#1A1A1A]">{formData.active ? 'Active' : 'Inactive'}</span>
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="hidden"
              />
            </label>
          </div>

        </div>

        {/* Right Column */}
        <div className="flex w-full flex-col gap-6 lg:w-[55%] rounded-[16px] bg-white p-[24px] shadow-sm">

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.showOnHomepage}
              onChange={(e) => setFormData({ ...formData, showOnHomepage: e.target.checked })}
              className="size-5 rounded-[4px] border-[#E0E0E0] text-[#FF6A1A] focus:ring-[#FF6A1A] cursor-pointer"
            />
            <span className="font-host text-[14px] font-bold text-[#1A1A1A]">Show on Homepage</span>
          </label>

          <HighlightBlock
            title="Features"
            addLabel="Add Feature"
            availableItems={availableFeatures}
            setAvailableItems={setAvailableFeatures}
            selectedItems={formData.features}
            setSelectedItems={(fn) => setFormData(p => ({ ...p, features: typeof fn === 'function' ? fn(p.features) : fn }))}
          />

          <div className="mt-4">
            <HighlightBlock
              title="Excluded Features"
              addLabel="Add Excluded Feature"
              availableItems={availableExcludedFeatures}
              setAvailableItems={setAvailableExcludedFeatures}
              selectedItems={formData.excludedFeatures}
              setSelectedItems={(fn) => setFormData(p => ({ ...p, excludedFeatures: typeof fn === 'function' ? fn(p.excludedFeatures) : fn }))}
            />
          </div>

          {/* Vehicle Assignment */}
          <div className="mt-4">
            <h2 className="font-host text-[14px] font-bold text-[#1A1A1A] mb-4">Vehicle Assignment</h2>
            <div className="flex flex-wrap gap-3">
              {vehicleOptions.map(v => (
                <label key={v} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.vehicles.includes(v)}
                    onChange={() => toggleVehicle(v)}
                    className="size-[18px] rounded-[4px] border-[#E0E0E0] text-[#FF6A1A] focus:ring-[#FF6A1A] cursor-pointer"
                  />
                  <div className="flex h-[36px] items-center rounded-[18px] border border-[#E0E0E0] px-4 font-host text-[13px] text-[#1A1A1A]">
                    {v}
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <HighlightBlock
              title="Good to know"
              availableItems={availableGoodToKnow}
              setAvailableItems={setAvailableGoodToKnow}
              selectedItems={formData.goodToKnow}
              setSelectedItems={(fn) => setFormData(p => ({ ...p, goodToKnow: typeof fn === 'function' ? fn(p.goodToKnow) : fn }))}
            />
          </div>

          {/* Service Image Strip */}
          <div className="mt-4 flex flex-col gap-4">
            <h2 className="font-host text-[14px] font-bold text-[#1A1A1A]">Service Image</h2>
            <div className="flex flex-col sm:flex-row gap-4 bg-[#FAFAFA] p-4 rounded-[12px] border border-[#F0F0F0]">
              <div
                className="group relative flex h-[120px] sm:w-[220px] flex-col items-center justify-center rounded-[12px] border-2 border-dashed border-[#B2D873] bg-[#F9FBE7] transition-colors hover:bg-[#F1F8E9] cursor-pointer overflow-hidden"
                onClick={() => mainFileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={mainFileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={e => handleImageUpload(e, true)}
                />
                {formData.images[0] ? (
                  <>
                    <img
                      src={formData.images[0]}
                      alt="Main preview"
                      className="h-full w-full object-cover"
                    />
                    {renderRemoveImageButton(0)}
                  </>
                ) : (
                  <>
                    <div className="flex size-[32px] items-center justify-center rounded-[8px] bg-[#1A1A1A] mb-2">
                      <ImageIcon className="text-white" size={16} />
                    </div>
                    <span className="font-host text-[12px] text-[#555555]">Drag & Drop images or</span>
                    <span className="font-host text-[12px] font-semibold text-[#1E88E5]">Browse Files</span>
                  </>
                )}
              </div>
              <div className="flex flex-col justify-center">
                <input
                  type="file"
                  ref={thumbFileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={e => handleImageUpload(e, false)}
                />
                <div className="flex gap-2">
                  {renderThumbnails()}
                </div>
                <div className="flex flex-col mt-2">
                  <span className="font-host text-[11px] font-semibold text-[#1A1A1A]">Upload up to 5 Images</span>
                  <span className="font-host text-[11px] text-[#757575]">Supported: JPG, PNG</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Footer Actions */}
      <div className="mt-8 flex items-center justify-between pb-8">
        <button
          type="button"
          onClick={() => navigate(dashboardRoutes.services)}
          className="flex h-[44px] items-center justify-center rounded-[8px] border border-[#E0E0E0] bg-white px-[48px] font-host text-[15px] font-bold text-[#1A1A1A] transition-colors hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="flex h-[44px] items-center justify-center rounded-[8px] bg-[#334E1C] px-[48px] font-host text-[15px] font-bold text-white transition-colors hover:bg-[#253914]"
        >
          {isEditing ? 'Save Changes' : 'Create Service'}
        </button>
      </div>
    </DashboardLayout>
  )
}